import { FC, useEffect, useState } from "react"
import { Tabs } from 'antd';
import { useTranslation } from "react-i18next";
import modalStyles from '../../../components/modalWindows/styles/BaseModalWindow.module.scss';
import { NumberInput, TextInput } from "../../../components/controls/inputs";
import GridWrapper from "../../../components/controls/GridWrapper";
import FlexColumn from "../../../components/controls/FlexColumn";
import BaseCreator from "../../../components/creators/baseCreator";
import { ILoyaltyProgramEditDTO, ILoyaltyProgramViewDTO } from "../../../libs/coreapi-dto/dirs/loyaltyProgram";
import { useForm } from "../../../system/hooks/useForm";
import { useLoyaltyProgram } from "./useLoyaltyProgram";
import { Checkbox } from "antd";
import { IValidator } from "./interface";
import styles from './styles/LoyaltyProgram.module.scss'
import CompatibilityTabs from "./components/CompatybilityTabs";
import { IEntitySimpleDTO } from "../../../libs/coreapi-dto/@types/common";
import { ContractorDataProvider } from "../../../Services/DataProviders/ContractorDataProvider";
import { useAppContext } from "../../../system/providers/appContextProvider";
import GridSelectorInput from "../../../components/controls/GridSelectorInput";
import { ContractorSelectorModal } from "../../Dictionaries/Contractor";
import { StoreSelectorModal } from "../../Dictionaries/Store";
import { useUserContext } from "../../../system/providers/userContextProvider";

const LoyaltyProgramCreatorModal: FC<ICreatorModalProps<ILoyaltyProgramEditDTO>> = (props) => {
    const {
        program,
        parsedProgram,
        updateProgramName,
        updateProgramType,
        updateContractorIdGlobal,
        updateProgramEnabled,
        updateUrl,
        updatePinCode,
        updateCardPrefix,
        updateStoreDepartmentId,
        updateStoreDepartmentKey,
        updateTimeout,
        updateIsAutoSendAllCheques,
        updateProxyUse,
        updateProxyPort,
        updateProxyAddress,
        updateProxyUser,
        updateProxyPassword,
        updateSmsTemplate,
        updateSmsValidationTemplate,
        updateSmsDebitTemplate,
        updateIdActionGroup,
        updateMinSumForDebit,
        updateDebitPermitBySms,
        updateUsePersonalAdditionSale,
        updateCompatibility,
        setIncompatibleActions,
        setIncompatibleTypeCards,
        setIncompatiblePrograms,
    } = useLoyaltyProgram(props.data ?? {} as ILoyaltyProgramViewDTO);

    const appContext = useAppContext();
    const userContext = useUserContext()

    const contractorDP = new ContractorDataProvider(appContext.coreApiService);
    const isAdministrator = userContext.userRoles.includes('Administrator');

    const { t } = useTranslation();
    const baseT = (value: string) => t('directory.loyaltyProgram.' + value)
    const errorsT = (value: string) => t('errors.' + value);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<string>('1');
    
    const [smsTemplateError, setSmsTemplateError] = useState<string>('');
    const [smsValidationTemplateError, setSmsValidationTemplateError] = useState<string>('');
    const [smsDebitTemplateError, setSmsDebitTemplateError] = useState<string>('');
    const [contractor, setContractor] = useState<IEntitySimpleDTO | null>(null);
    
    useEffect(() => {
        if (contractor) {
            updateContractorIdGlobal(contractor.idGlobal);
        } else {
            updateContractorIdGlobal('');
        }
    }, [contractor]);

    const { isValid, errors, setErrors } = useForm<IValidator>({
        validations: {
            name: {
                required: {
                    value: program.name ? false : true,
                    message: errorsT("required")
                }
            },
            type: {
                required: {
                    value: program.type ? false : true,
                    message: errorsT("required")
                }
            },
            idContractorGlobal: {
                required: {
                    value: program.idContractorGlobal ? false : true,
                    message: errorsT("required")
                }
            },
            Url: {
                required: {
                    value: parsedProgram.parsedSettings.Url ? false : true,
                    message: errorsT("required")
                }
            },
            StoreDepartmentKey: {
                required: {
                    value: parsedProgram.parsedSettings.StoreDepartmentKey ? false : true,
                    message: errorsT("required")
                }
            },
            StoreDepartmentId: {
                required: {
                    value: parsedProgram.parsedSettings.StoreDepartmentId ? false : true,
                    message: errorsT("required")
                }
            },
            PinCode: {
                required: {
                    value: parsedProgram.parsedSettings.PinCode ? false : true,
                    message: errorsT("required")
                }
            },
            MinSumForDebit: {
                required: {
                    value: parsedProgram.parsedParams.DebitPermitBySms && !parsedProgram.parsedParams.MinSumForDebit,
                    message: baseT("MinSumForDebitError")
                }
            },
            SmsDebitTemplate: {
                required: {
                    value: parsedProgram.parsedParams.DebitPermitBySms && !parsedProgram.parsedParams.SmsDebitTemplate,
                    message: errorsT("required")
                }
            }
        }
    });

    const validateSmsTemplate = (value: string): boolean => {
        if (!value) {
            setSmsTemplateError(errorsT("required"));
            return false;
        }
        if (!value.includes('$[sms_code]')) {
            setSmsTemplateError(baseT('smsCodeRequired'));
            return false;
        }
        setSmsTemplateError('');
        return true;
    };

    const validateSmsValidationTemplate = (value: string): boolean => {
        if (!value) {
            setSmsValidationTemplateError(errorsT("required"));
            return false;
        }
        if (!value.includes('$[sms_code]')) {
            setSmsValidationTemplateError(baseT('smsCodeRequired'));
            return false;
        }
        setSmsValidationTemplateError('');
        return true;
    };

    const validateSmsDebitTemplate = (value: string): boolean => {
        if (parsedProgram.parsedParams.DebitPermitBySms) {
            if (!value) {
                setSmsDebitTemplateError(errorsT("required"));
                return false;
            }
            if (!value.includes('$[sms_code]')) {
                setSmsDebitTemplateError(baseT('smsCodeRequired'));
                return false;
            }
        }
        setSmsDebitTemplateError('');
        return true;
    };

    const handleSmsTemplateChange = (value: string) => {
        updateSmsTemplate(value);
        validateSmsTemplate(value);
    };

    const handleSmsValidationTemplateChange = (value: string) => {
        updateSmsValidationTemplate(value);
        validateSmsValidationTemplate(value);
    };

    const handleSmsDebitTemplateChange = (value: string) => {
        updateSmsDebitTemplate(value);
        validateSmsDebitTemplate(value);
    };

    const handleDebitPermitBySmsChange = (checked: boolean) => {
        updateDebitPermitBySms(checked);
        if (checked) {
            validateSmsDebitTemplate(parsedProgram.parsedParams.SmsDebitTemplate);
        } else {
            setSmsDebitTemplateError('');
        }
    };

    const validateAllFields = (): boolean => {
        const isSmsTemplateValid = validateSmsTemplate(parsedProgram.parsedParams.SmsTemplate);
        const isSmsValidationTemplateValid = validateSmsValidationTemplate(parsedProgram.parsedParams.SmsValidationTemplate);
        const isSmsDebitTemplateValid = validateSmsDebitTemplate(parsedProgram.parsedParams.SmsDebitTemplate);
    
        return isSmsTemplateValid && isSmsValidationTemplateValid && isSmsDebitTemplateValid;
    };
    
    const handleSubmit = () => {
          
        if (validateAllFields() && isValid()) {
            props.save({ ...program })
            setIsSubmitting(true)
        } else {
            console.log(baseT('validationFailed'));
        }
    }

    useEffect(() => {
        validateSmsDebitTemplate(parsedProgram.parsedParams.SmsDebitTemplate);
    }, [parsedProgram.parsedParams.DebitPermitBySms]);
    
    const handleIncompatibleDiscount2ProgramsChange = (incompatibleActionIds: string[]) => {
        setIncompatibleActions(incompatibleActionIds);
    };

    const handleIncompatibleDiscount2CardTypeChange = (incompatibleTypeCardIds: string[]) => {
        setIncompatibleTypeCards(incompatibleTypeCardIds);
    };

    const handleIncompatibleProgramsChange = (incompatibleProgramIds: string[]) => {
        setIncompatiblePrograms(incompatibleProgramIds);
    };

    useEffect(() => {
        if(!program.idContractorGlobal) return
        contractorDP.getById(program.idContractorGlobal, (data) => {
            setContractor({idGlobal: data.idGlobal, displayName: data.name})
        })
    }, [])

    const tabItems = [
        {
            key: '1',
            label: baseT('basicSettings'),
            children: (
                <FlexColumn>
                    <GridWrapper cols={2}>
                        <TextInput
                            required
                            inline
                            label={baseT('programName')}
                            value={program.name}
                            error={errors.name}
                            onChange={updateProgramName}
                        />
                        <TextInput
                            required
                            inline
                            label={baseT('typeProgram')}
                            value={parsedProgram.type == 16 ? 'SailPlay' : ''}
                            error={errors.type}
                            onChange={updateProgramName}
                        />
                        {/* <NumberInput
                            required
                            label={baseT('typeProgram')}
                            inline
                            value={parsedProgram.type}
                            min={0}
                            onChange={updateProgramType}
                            error={errors.type}
                        /> */}

                    </GridWrapper>
                    <GridSelectorInput
                        required
                        disabled={isAdministrator ? false : true}
                        variant="required"
                        selectorModalJsx={ContractorSelectorModal}
                        className={styles.field_Provider}
                        inline
                        id={"dictionaryInputInvoiceSupplier"}
                        label={baseT('pharmacyName')}
                        selectedEntity={contractor}
                        onSelect={(value) => setContractor({ ...value })}
                        onClear={() => setContractor(null)}
                        error={errors.idContractorGlobal}
                        onFocus={() => setErrors({ ...errors, idContractorGlobal: undefined })}
                        treeViewCheckDirectoryType="pharmacy"
                    />

                    <TextInput
                        required
                        inline
                        label={baseT('url')}
                        value={parsedProgram.parsedSettings.Url}
                        error={errors.Url}
                        onChange={updateUrl}
                    />
                    <GridWrapper cols={2}>
                        <TextInput
                            required
                            inline
                            label={baseT('storeDepartmentId')}
                            value={parsedProgram.parsedSettings.StoreDepartmentId}
                            error={errors.StoreDepartmentId}
                            onChange={updateStoreDepartmentId}
                        />
                        <TextInput
                            label={baseT('cardPrefix')}
                            inline
                            value={parsedProgram.parsedSettings.CardPrefix}
                            onChange={updateCardPrefix}
                        />
                    </GridWrapper>
                    <GridWrapper cols={2}>
                        <TextInput
                            required
                            inline
                            label={baseT('key')}
                            value={parsedProgram.parsedSettings.StoreDepartmentKey}
                            error={errors.StoreDepartmentKey}
                            onChange={updateStoreDepartmentKey}
                        />
                    </GridWrapper>
                    <GridWrapper cols={2}>
                        <TextInput
                            required
                            inline
                            label={baseT('pinCode')}
                            value={parsedProgram.parsedSettings.PinCode}
                            error={errors.PinCode}
                            onChange={updatePinCode}
                        />
                    </GridWrapper>
                    <GridWrapper cols={2}>
                        <NumberInput
                            label={baseT('timeout')}
                            inline
                            value={parsedProgram.parsedSettings.Timeout}
                            min={0}
                            onChange={updateTimeout}
                        />
                    </GridWrapper>
                    <GridWrapper cols={2}>
                        <div className={styles.checkBoxWrapper}>
                            <Checkbox
                                checked={parsedProgram.parsedSettings.IsAutoSendAllCheques}
                                onChange={(e) => updateIsAutoSendAllCheques(e.target.checked)}
                                name={"IsAutoSendAllCheques"}
                                style={{ alignSelf: 'center' }}
                            >
                                {baseT('isAutoSendAllCheques')}
                            </Checkbox>
                        </div>
                    </GridWrapper>
                    <GridWrapper cols={2}>
                        <div className={styles.checkBoxWrapper}>
                            <Checkbox
                                checked={parsedProgram.parsedSettings.Proxy.Use}
                                onChange={(e) => updateProxyUse(e.target.checked)}
                                name={"Use"}
                                style={{ alignSelf: 'center', height: 36 }}
                            >
                                {baseT('useProxy')}
                            </Checkbox>
                        </div>
                    </GridWrapper>
                    {parsedProgram.parsedSettings.Proxy.Use && (
                        <div className={styles.proxySettingsWrapper}>
                            <div className={styles.proxySettingsTitleWrapper}>
                                {baseT('proxySettings')}
                            </div>
                            <TextInput
                                label={baseT('proxyAddress')}
                                inline
                                value={parsedProgram.parsedSettings.Proxy.Address || ''}
                                onChange={updateProxyAddress}
                            />
                            <GridWrapper cols={3}>
                                <NumberInput
                                    label={baseT('proxyPort')}
                                    inline
                                    value={parsedProgram.parsedSettings.Proxy.Port}
                                    min={0}
                                    onChange={updateProxyPort}
                                />
                                <TextInput
                                    label={baseT('proxyUser')}
                                    inline
                                    value={parsedProgram.parsedSettings.Proxy.User || ''}
                                    onChange={updateProxyUser}
                                />
                                <TextInput
                                    label={baseT('proxyPassword')}
                                    inline
                                    type="password"
                                    value={parsedProgram.parsedSettings.Proxy.Password || ''}
                                    onChange={updateProxyPassword}
                                />
                            </GridWrapper>
                        </div>
                    )}
                    <TextInput
                        required
                        placeholder="Для подтверждения регистрации и согласия на получение уведомлений используйте смс код $[sms_code]"
                        inline
                        label={baseT('smsTemplate')}
                        value={parsedProgram.parsedParams.SmsTemplate}
                        error={smsTemplateError}
                        onChange={handleSmsTemplateChange}
                    />
                    <TextInput
                        required
                        placeholder="Получен запрос на измнение данных. Код: $[sms_code]"
                        inline
                        label={baseT('smsValidationTemplate')}
                        value={parsedProgram.parsedParams.SmsValidationTemplate}
                        error={smsValidationTemplateError}
                        onChange={handleSmsValidationTemplateChange}
                    />
                    <div className={styles.checkBoxWrapper}>
                        <Checkbox
                            checked={program.isEnabled}
                            onChange={(e) => updateProgramEnabled(e.target.checked)}
                            name={"isEnabled"}
                            style={{ alignSelf: 'center' }}
                        >
                            {baseT('isEnabled')}
                        </Checkbox>
                    </div>
                </FlexColumn>
                
            )
        },
        {
            key: '2',
            label: baseT('params'),
            children: (
                <FlexColumn>
                    <TextInput
                        label={baseT('iDActionGroup')}
                        inline
                        value={parsedProgram.parsedParams.IDActionGroup}
                        onChange={updateIdActionGroup}
                    />
                    <div className={styles.checkBoxWrapper} style={{ gap: 8, marginBottom: 10 }}>
                        <span>{baseT('debitPermitBySms')}</span>
                        <Checkbox
                            checked={parsedProgram.parsedParams.DebitPermitBySms}
                            onChange={(e) => handleDebitPermitBySmsChange(e.target.checked)}
                            name={"DebitPermitBySms"}
                        />
                    </div>
                    {parsedProgram.parsedParams.DebitPermitBySms && (
                        <>
                            <TextInput
                                required={true}
                                placeholder="Для подтверждения согласия на списание баллов используйте код $[sms_code]"
                                label={baseT('smsDebitTemplate')}
                                inline
                                error={smsDebitTemplateError}
                                value={parsedProgram.parsedParams.SmsDebitTemplate}
                                onChange={handleSmsDebitTemplateChange}
                            />
                            <NumberInput
                                required={true}
                                label={baseT('minSumForDebit')}
                                inline
                                value={parsedProgram.parsedParams.MinSumForDebit}
                                error={errors.MinSumForDebit}
                                min={1}
                                onChange={updateMinSumForDebit}
                            />
                        </>
                    )}
                    <div className={styles.checkBoxWrapper} style={{ gap: 8 }}>
                        <span>{baseT('usePersonalAdditionSale')}</span>
                        <Checkbox
                            checked={parsedProgram.parsedParams.UsePersonalAdditionSale}
                            onChange={(e) => updateUsePersonalAdditionSale(e.target.checked)}
                            name={"UsePersonalAdditionSale"}
                        />
                    </div>
                </FlexColumn>
            )
        },
        {
            key: '3',
            label: baseT('compatibility'),
            children: (
                <FlexColumn>
                    <GridWrapper cols={1}>
                        <div className={styles.checkBoxWrapper} style={{marginBottom: 10}}>
                            <Checkbox
                                checked={program.compatibility}
                                onChange={(e) => updateCompatibility(e.target.checked)}
                                name={"compatibility"}
                            >
                                {baseT('compatibilityTitle')}
                            </Checkbox>

                        </div>
                    </GridWrapper>
                    {program.compatibility && (
                        <CompatibilityTabs
                            data={props.data}
                            onIncompatibleDiscount2ProgramsChange={handleIncompatibleDiscount2ProgramsChange}
                            onIncompatibleDiscount2CardTypeChange={handleIncompatibleDiscount2CardTypeChange}
                            onIncompatibleProgramsChange={handleIncompatibleProgramsChange}
                        />
                    )}
                </FlexColumn>
            )
        },
    ];

    return (
        <BaseCreator
            variant={props.variant}
            isSubmitting={isSubmitting}
            save={handleSubmit}
            modalWindowClassName={modalStyles.flexModalWindow}
            cancel={props.cancel}
            valid={!props.lockFromPermission}
        >
            <div className={styles.modalContentWindow}>
                <Tabs
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    type="card"
                    items={tabItems}
                    style={{
                        borderRadius: 4
                    }}
                />
            </div>
        </BaseCreator>
    )
}

export default LoyaltyProgramCreatorModal